export type DeepPartial<T> = {
	[K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

export const mergeWithDefault = <T extends Record<string, any>>(
	newData: DeepPartial<T> | null,
	oldData: DeepPartial<T>,
	defaultData: T,
): T => {
	const safeNew: DeepPartial<T> = newData ?? {};
	const safeOld: DeepPartial<T> = oldData ?? {};

	const result = {} as T;

	for (const key of Object.keys(defaultData) as (keyof T)[]) {
		const defVal = defaultData[key];
		const newVal = safeNew[key];
		const oldVal = safeOld[key];

		const isObject =
			defVal && typeof defVal === "object" && !Array.isArray(defVal);

		if (isObject) {
			result[key] = mergeWithDefault(
				(newVal ?? null) as DeepPartial<T[typeof key]> | null,
				(oldVal ?? {}) as DeepPartial<T[typeof key]>,
				defVal as T[typeof key],
			);
		} else if (newVal !== undefined && newVal !== null) {
			result[key] = newVal as T[typeof key];
		} else if (oldVal !== undefined) {
			result[key] = oldVal as T[typeof key];
		} else {
			result[key] = defVal;
		}
	}
	return result;
};

export default function deepParse(object) {
	if (typeof object === "string") {
		try {
			const parsed = JSON.parse(object);
			if (typeof parsed === "object" && parsed !== null) {
				return deepParse(parsed);
			}
			return parsed;
		} catch (e) {
			return object;
		}
	} else if (Array.isArray(object)) {
		return object.map(deepParse);
	} else if (typeof object === "object" && object !== null) {
		return Object.keys(object).reduce((acc, key) => {
			acc[key] = deepParse(object[key]);
			return acc;
		}, {});
	}
	return object;
}
